import { refreshTokenApi } from "@/api/services/main/auth";
import { ConfirmReloadModal } from "@/components/commons/ConfirmReloadModal";
import { LoadingScreen } from "@/components/elements/Loading/Loading";
import { useTokenClaims } from "@/features/auth/hooks/useTokenClaims";
import { useDialog } from "@/hooks/useDialog";
import { ClientRoutes } from "@/routes/config";
import { setAxiosAuth } from "@/utils/api";
import { redirectToLogin } from "@/utils/redirectToLogin";
import { getSessionIdentity, getToken, verifyToken } from "@/utils/token";
import { Typography } from "@mui/material";
import { FC, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export const withAuth: <CP extends {}>(WrappedComponent: FC<CP>) => FC<CP> = (WrappedComponent) => (props) => {
  const { pathname } = useLocation();
  const [isVerified, setIsVerified] = useState(false);
  const { claims } = useTokenClaims();
  const navigate = useNavigate();
  const confirmReload = useDialog();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // First email seen on this mount — the baseline the running session is compared against
  const sessionEmail = useRef("");

  useEffect(() => {
    if (!sessionEmail.current && claims.email) {
      sessionEmail.current = claims.email;
    }
  }, [claims.email]);

  useEffect(() => {
    if (!sessionEmail.current || !claims.email) return;

    // Every 5 seconds check the token owner, if it has changed (logged in as
    // someone else in another tab) then redirect to the login page. The claims
    // are re-read from the cookie on each tick — a cookie written by another tab
    // does not re-render this component, so the render-time value would be stale.
    intervalRef.current = setInterval(() => {
      const { email } = getSessionIdentity();
      if (email && sessionEmail.current !== email) {
        redirectToLogin();
      }
    }, 5000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [claims.email]);

  useEffect(() => {
    let cancelled = false;

    // check version build
    const checkVersion = async () => {
      try {
        const response = await fetch("/version.json", {
          headers: { "Cache-Control": "no-cache" },
        });
        const { version } = await response.json();
        const currentVersion = localStorage.getItem("app-version");

        // show modal and update version
        if (version && (!currentVersion || version !== parseInt(currentVersion))) {
          localStorage.setItem("app-version", version);
          const agree = await confirmReload.show();
          if (agree) {
            window.location.reload();
          }
        }
      } catch (error) {
        console.error("Error checking version:", error);
      }
    };

    const verifyAuth = async () => {
      const { accessToken, refreshToken } = getToken();
      setAxiosAuth();
      let isAuthenticated = !!accessToken && verifyToken(accessToken);

      // Access token missing/expired but a refresh token is still present: mint a
      // fresh pair instead of bouncing to login. This keeps the user signed in
      // across browser restarts and expired access tokens (persistent cookies).
      if (!isAuthenticated && accessToken && refreshToken) {
        try {
          await refreshTokenApi({ accessToken, refreshToken });
          setAxiosAuth();
          isAuthenticated = true;
        } catch {
          isAuthenticated = false;
        }
      }

      if (cancelled) return;

      if (!isAuthenticated && ClientRoutes.some((path) => pathname.startsWith(path))) {
        redirectToLogin({ clearToken: true });
        return;
      }

      setIsVerified(true);
      checkVersion();
    };

    verifyAuth();

    return () => {
      cancelled = true;
    };
  }, [pathname, navigate, redirectToLogin]);

  if (!isVerified) return <LoadingScreen />;

  return (
    <>
      <ConfirmReloadModal open={confirmReload.open} onOk={confirmReload.confirm}>
        <Typography>システムがアップデートされました。最新の状態にするため、ページをリロードしてください。</Typography>
      </ConfirmReloadModal>
      <WrappedComponent {...props} />
    </>
  );
};
